/**
 * Shared CRM type constants and utilities
 * Used by both frontend and backend for consistent CRM type handling
 */

/**
 * CRM type enum values - matches Prisma CrmType enum
 */
export const CrmTypeEnum = {
  CLIO: "CLIO",
  FILEVINE: "FILEVINE",
  LITIFY: "LITIFY",
  NEOS: "NEOS",
  SMART_ADVOCATE: "SMART_ADVOCATE",
  TORTICITY: "TORTICITY",
} as const;

export type CrmType = (typeof CrmTypeEnum)[keyof typeof CrmTypeEnum];

/**
 * How a data source gets data into RAP, grouped for UI purposes.
 * PULL = native CRM we sync from; PUSH = client pushes JSON to us;
 * SPREADSHEET = spreadsheet ingestion (manual upload or URL pull).
 */
export type DataSourceFamily = "PULL" | "PUSH" | "SPREADSHEET";

/**
 * Whether (and how) a source supports an on-demand sync.
 * PULL = native CRM sync; URL_PULL = re-fetch of a spreadsheet URL;
 * NONE = data arrives on its own (push / manual upload), nothing to trigger.
 */
export type SyncCapability = "NONE" | "PULL" | "URL_PULL";

export interface CrmTypeInfo {
  family: DataSourceFamily;
  /** Canonical human-readable label — the ONE spelling every surface shows. */
  label: string;
  sync: SyncCapability;
}

/**
 * SINGLE SOURCE OF TRUTH for per-crmType facts (label, family, sync capability).
 * Every derived helper — frontend familyFor/capabilitiesFor/humanizeCrmType/kindLabel/
 * syncModeFor and the CRM_TYPE_LABELS map below — reads from here. Adding a new source
 * type means adding ONE entry; never re-encode these facts in another map.
 */
export const CRM_TYPE_INFO = {
  CLIO: { family: "PULL", label: "Clio", sync: "PULL" },
  FILEVINE: { family: "PULL", label: "Filevine", sync: "PULL" },
  LITIFY: { family: "PULL", label: "Litify", sync: "PULL" },
  NEOS: { family: "PULL", label: "Neos", sync: "PULL" },
  PUSH_JSON: { family: "PUSH", label: "Push", sync: "NONE" },
  SMART_ADVOCATE: { family: "PULL", label: "Smart Advocate", sync: "PULL" },
  SPREADSHEET_UPLOAD: { family: "SPREADSHEET", label: "Spreadsheet upload", sync: "NONE" },
  SPREADSHEET_URL: { family: "SPREADSHEET", label: "Spreadsheet URL", sync: "URL_PULL" },
  TORTICITY: { family: "PULL", label: "Torticity", sync: "PULL" },
} as const satisfies Record<string, CrmTypeInfo>;

/** Any crmType with a CRM_TYPE_INFO entry (pull CRMs + ingestion kinds). */
export type KnownSourceType = keyof typeof CRM_TYPE_INFO;

/**
 * Resolve the canonical info for a crmType.
 *
 * Unknown/future types get ONE documented default everywhere: they are treated as a
 * pull CRM (family "PULL", sync "PULL") and the raw value is echoed as the label.
 * Do not invent a different default in a consumer.
 */
export function crmTypeInfo(type: null | string | undefined): CrmTypeInfo {
  if (type != null) {
    const known = (CRM_TYPE_INFO as Record<string, CrmTypeInfo>)[type];
    if (known) return known;
  }
  return { family: "PULL", label: type ?? "", sync: "PULL" };
}

/**
 * Human-readable labels for CRM / ingestion source types — derived from CRM_TYPE_INFO.
 */
export const CRM_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(CRM_TYPE_INFO).map(([type, info]) => [type, info.label])
);

/**
 * Format a CRM type string to a human-readable label
 * @param type - The CRM type string (e.g., 'SMART_ADVOCATE')
 * @returns Human-readable label (e.g., 'Smart Advocate')
 */
export function formatCrmType(type: string): string {
  return CRM_TYPE_LABELS[type] || type;
}

/**
 * Type-constrained provider key mapping (lowercase for provider lookups)
 * Maps CrmType enum values to lowercase provider keys used in auth configuration JSON.
 */
export const CRM_PROVIDER_KEYS = {
  CLIO: "clio",
  FILEVINE: "filevine",
  LITIFY: "litify",
  NEOS: "neos",
  SMART_ADVOCATE: "smart_advocate",
  TORTICITY: "torticity",
} as const satisfies Record<CrmType, string>;

/**
 * Array of all provider keys - derived from CRM_PROVIDER_KEYS
 */
export const CRM_PROVIDERS = Object.values(CRM_PROVIDER_KEYS);

/**
 * Union type of all provider keys - derived from CRM_PROVIDER_KEYS
 */
export type CrmProviderKey = (typeof CRM_PROVIDER_KEYS)[CrmType];

/**
 * Ingestion source types — push/spreadsheet sources that are stored as CrmConfig
 * rows but are NOT pull CRMs. They have no upstream API, so pull-CRM-only concepts
 * (case types, field discovery, credential tests) don't apply to them. Anything
 * that branches on "is this a real CRM" should go through isIngestionCrmType.
 */
export const INGESTION_CRM_TYPES = [
  "PUSH_JSON",
  "SPREADSHEET_UPLOAD",
  "SPREADSHEET_URL",
] as const satisfies readonly KnownSourceType[];

/**
 * True when a crmType is an ingestion source (push/spreadsheet), not a pull CRM.
 * Derived from CRM_TYPE_INFO's family — unknown types default to pull (false).
 */
export function isIngestionCrmType(type: null | string | undefined): boolean {
  return type != null && crmTypeInfo(type).family !== "PULL";
}

/**
 * Maps each CrmType / ingestion source type to the flat top-level key under which a
 * StandardCustomField.metadata JSON records its per-provider field path (e.g.
 * `metadata.neosField`, `metadata.spreadsheetField`). Single source of truth shared by
 * frontend and backend — mirrors the backend's CRM_METADATA_KEYS in
 * backend/src/lib/crm/shared/pipeline/field-metadata.ts.
 *
 * Note: "CUSTOM" is intentionally absent — custom syncers store paths in a nested
 * `metadata.customFields[syncerKey]` bucket rather than a flat top-level key.
 */
export const CRM_METADATA_KEYS: Record<string, string> = {
  CLIO: "clioField",
  FILEVINE: "fileVineField",
  LITIFY: "litifyField",
  NEOS: "neosField",
  PUSH_JSON: "pushJsonField",
  SMART_ADVOCATE: "smartAdvocateField",
  SPREADSHEET_UPLOAD: "spreadsheetField",
  SPREADSHEET_URL: "spreadsheetField",
  TORTICITY: "torticityField",
};

/**
 * Get the flat metadata key name for a CRM / ingestion source type, or undefined for an
 * unknown/missing type (e.g. CUSTOM, which uses the nested `customFields` bucket instead).
 *
 * @example getCrmMetadataKey("SPREADSHEET_UPLOAD") // "spreadsheetField"
 */
export function getCrmMetadataKey(type: null | string | undefined): string | undefined {
  if (type == null) return undefined;
  return CRM_METADATA_KEYS[type];
}
